/**
 * useKeyboardListNav — arrow-key navigation for an ordered list of items.
 *
 * Returns a `KeyboardEvent` handler suitable for placing on a parent element
 * (or each item) that implements roving-tabindex semantics. The hook itself
 * is presentation-agnostic; consumers decide which keys to wire up.
 *
 * Supported keys:
 *   - ArrowDown / ArrowRight — move to next item
 *   - ArrowUp / ArrowLeft — move to previous item
 *   - Home — first item
 *   - End — last item
 *
 * Wraps at the boundaries so navigation forms a cycle.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";

export type ListNavOrientation = "horizontal" | "vertical" | "both";

export interface UseKeyboardListNavParams<T extends string> {
  /** Ordered identifiers of items in the list. Empty = no-op. */
  readonly items: readonly T[];
  /** Currently active identifier; used as the cursor anchor. */
  readonly activeId: T;
  /** Called with the next id when the user presses an arrow / Home / End. */
  readonly onSelect: (id: T) => void;
  /** Which arrows are intercepted (default: vertical). */
  readonly orientation?: ListNavOrientation;
}

interface UseKeyboardListNavResult {
  readonly onKeyDown: (event: ReactKeyboardEvent) => void;
}

/**
 * Build a keyboard handler that moves the active selection through `items`.
 *
 * The hook does **not** focus DOM nodes — that is the consumer's job (typically
 * via a `ref` map keyed by `id`). The hook only computes which id should
 * become active and dispatches `onSelect`.
 */
export function useKeyboardListNav<T extends string>(
  params: UseKeyboardListNavParams<T>,
): UseKeyboardListNavResult {
  const { items, activeId, onSelect, orientation = "vertical" } = params;

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent): void => {
      if (items.length === 0) return;
      const wantsVertical = orientation === "vertical" || orientation === "both";
      const wantsHorizontal = orientation === "horizontal" || orientation === "both";

      const isNext =
        (wantsVertical && event.key === "ArrowDown") ||
        (wantsHorizontal && event.key === "ArrowRight");
      const isPrev =
        (wantsVertical && event.key === "ArrowUp") ||
        (wantsHorizontal && event.key === "ArrowLeft");
      const isFirst = event.key === "Home";
      const isLast = event.key === "End";

      if (!isNext && !isPrev && !isFirst && !isLast) return;
      event.preventDefault();

      const currentIdx = items.indexOf(activeId);
      const lastIdx = items.length - 1;

      let nextIdx: number;
      if (isFirst) {
        nextIdx = 0;
      } else if (isLast) {
        nextIdx = lastIdx;
      } else if (currentIdx < 0) {
        nextIdx = isNext ? 0 : lastIdx;
      } else if (isNext) {
        nextIdx = currentIdx === lastIdx ? 0 : currentIdx + 1;
      } else {
        nextIdx = currentIdx === 0 ? lastIdx : currentIdx - 1;
      }

      const next = items[nextIdx];
      if (next !== undefined && next !== activeId) {
        onSelect(next);
      }
    },
    [items, activeId, onSelect, orientation],
  );

  return { onKeyDown };
}
